import { Form, Formik, FormikProps } from 'formik';
import { FC, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { TrackCustomEvents, TrackPageVisit, TrackPageVisitPanel, TrackUserClick, TrackUserClickPanel } from '../commons/tracking';
import { customValidation, mapEventsToRules, mapRulesToEvents } from '../commons/tracking/utils';
import { TrackWebsiteValue } from '@/features/dataSources/components/createDataSource/types';
import { useWorkspace } from '@/features/workspaces/hooks';
import { DataSourceCodeConfig, DataSource } from '@/models/dataSource';
import { RootState, useAppDispatch } from '@/reducers';
import { useDeleteRuleMutation, useLazyGetRulesByDataSourceIdQuery, useSaveRulesMutation } from '@/services/endpoints/rules';
import { stashRules } from '@/features/rules/rulesSlice';
import { StepPageActions } from '@/global/components';

type TrackWebsiteActivityStepProps = {
  onNextStep?: VoidFunction;
  onPrevStep?: VoidFunction;
  onCancel?: VoidFunction;
};

export const TrackWebsiteActivityStep: FC<TrackWebsiteActivityStepProps> = ({ onNextStep, onPrevStep }) => {
  const dispatch = useAppDispatch();
  const workspace = useWorkspace();
  const { t } = useTranslation('data_source_edit');

  const trackWebsiteActivityFormRef = useRef<FormikProps<TrackWebsiteValue>>(null);

  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as Partial<DataSource<DataSourceCodeConfig>> | undefined;
  const rules = useSelector((state: RootState) => state.rules.rulesList);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [deleteRule, _] = useDeleteRuleMutation();

  const initialValues: TrackWebsiteValue = { clickEvents: [], pageVisitEvents: [] };

  if (rules) {
    const events = mapRulesToEvents(rules, `${dataSource?.configuration?.url}/` || '');
    initialValues.clickEvents = [...events.clickEvents];
    initialValues.pageVisitEvents = [...events.pageVisitEvents];
  }

  const [saveRules, { data: createdRules, isLoading: isSaveRulesLoading }] = useSaveRulesMutation();
  const [getRulesByDataSourceId, rulesByDataSourceId] = useLazyGetRulesByDataSourceIdQuery();

  useEffect(() => {
    if (rulesByDataSourceId.data) {
      dispatch(stashRules(rulesByDataSourceId.data));
    }
  }, [rulesByDataSourceId.data]);

  useEffect(() => {
    if (createdRules) {
      onNextStep();
    }
  }, [createdRules]);

  const submitRules = (events: TrackWebsiteValue) => {
    const rules = mapEventsToRules(events);

    if (dataSource && dataSource.id) {
      const dataSourceId = dataSource.id;

      // adding new rules (without id)
      const createRules = rules.filter((rule) => rule.id === undefined);

      if (createRules) {
        saveRules({
          workspaceId: workspace.id,
          dataSourceId: dataSourceId,
          rules: createRules,
        });
      }
    }
  };

  const handleDeleteRule = (ruleId: string) => {
    if (ruleId && dataSource && dataSource.id) {
      deleteRule({
        workspaceId: workspace.id,
        dataSourceId: dataSource.id,
        ruleId: ruleId,
      });
    }
  };

  const handleNextStep = async () => {
    if (dataSource && dataSource.id) {
      getRulesByDataSourceId({
        dataSourceId: dataSource.id,
        workspaceId: workspace.id,
      });
    }
    await trackWebsiteActivityFormRef.current?.submitForm();

    const events = trackWebsiteActivityFormRef.current?.values;
    if (trackWebsiteActivityFormRef.current?.isValid && events) {
      submitRules(events);
    }
  };

  return (
    <>
      <div className='flex grow flex-col gap-8'>
        <div className='flex flex-col gap-2'>
          <h1 className='text-sm font-semibold text-gray-900'>{t('code.track_events.track_website_activity')}</h1>
          <h2 className='text-sm font-normal text-gray-600'>{t('code.track_events.user_perform_actions')}</h2>
        </div>

        <Formik
          onSubmit={() => {}}
          validate={(values) => customValidation(values, t)}
          initialValues={initialValues}
          validateOnBlur={false}
          validateOnChange={false}
          validateOnMount={false}
          innerRef={trackWebsiteActivityFormRef}
        >
          {({ values, setFieldValue, setFieldError, errors }) => {
            return (
              <Form className='flex flex-col gap-8'>
                <TrackUserClickPanel>
                  <TrackUserClick
                    values={values}
                    handleSetFieldValue={setFieldValue}
                    handleSetFieldError={setFieldError}
                    handleDeleteRule={handleDeleteRule}
                    errors={errors}
                  />
                </TrackUserClickPanel>
                <TrackPageVisitPanel>
                  <TrackPageVisit
                    values={values}
                    handleSetFieldValue={setFieldValue}
                    handleSetFieldError={setFieldError}
                    handleDeleteRule={handleDeleteRule}
                    errors={errors}
                    dataSource={dataSource}
                  />
                </TrackPageVisitPanel>
                <TrackCustomEvents />
              </Form>
            );
          }}
        </Formik>
      </div>

      <StepPageActions
        primaryButton={{
          text: 'COMMON:confirm',
          onClick: handleNextStep,
          isLoading: isSaveRulesLoading,
        }}
        secondaryButton={{
          text: 'COMMON:cancel',
          onClick: onPrevStep,
        }}
      />
    </>
  );
};
